// Phase 36 — Intelligence modes. Classifies the system's current state
// into one dominant mode and produces mode-specific language so the
// surface stops defaulting to "not translating into measurable outcomes."

import type {
  GapDetail,
  NearImpactItem,
  OverviewResponse,
  SignalCluster,
} from "../api.js";

export type IntelligenceModeKey =
  | "acceleration"
  | "suppression"
  | "fragmentation"
  | "coordination"
  | "momentum"
  | "emerging_opportunity"
  | "institutional_alignment"
  | "performative_activity"
  | "strategic_silence"
  // Phase 37 — new modes that name lower-strategic-relevance system
  // states explicitly, so the surface stops collapsing every quiet
  // window into "fragmentation."
  | "procedural_activity"
  | "symbolic_activity"
  | "weak_alignment"
  | "enforcement_mismatch";

export interface IntelligenceModeResolved {
  mode: IntelligenceModeKey;
  label: string;
  tone: "active" | "watch" | "blocked" | "quiet";
  headline: string;
  subline: string;
  focus: string | null;
  // Modules that drive this read — used to render the secondary rail
  // without inventing new data.
  driving_modules: string[];
  // Geo phrase used in the headline if a concentration is detected.
  geo_phrase: string | null;
}

interface ClassifyContext {
  modulesActive: Set<string>;
  modulesMeasurable: Set<string>;
  modulesNearImpact: Map<string, number>;
  clusters: SignalCluster[];
  strongestCluster: SignalCluster | null;
  geoCluster: SignalCluster | null;
  topGaps: GapDetail[];
  nearImpact: NearImpactItem[];
  realPct: number;
  narrativePct: number;
  totalSignals: number;
  measurableItems: number;
  blackItems: number;
  capitalPct: number;
  policyImpactPct: number;
  outcomeGaps: number;
  trendDelta: number;
}

function buildContext(data: OverviewResponse): ClassifyContext {
  const modulesActive = new Set<string>();
  const modulesMeasurable = new Set<string>();
  const modulesNearImpact = new Map<string, number>();
  for (const m of data.modules ?? []) {
    if (m.total_items > 0) modulesActive.add(m.module);
  }
  for (const slice of data.system_impact?.modules ?? []) {
    if (slice.measurable_items > 0) modulesMeasurable.add(slice.module);
  }
  for (const n of data.near_impact ?? []) {
    if (n.impact_proximity === "near") {
      modulesNearImpact.set(
        n.module,
        (modulesNearImpact.get(n.module) ?? 0) + 1,
      );
    }
  }
  const trend = data.trend_series ?? [];
  let trendDelta = 0;
  if (trend.length >= 2) {
    const half = Math.floor(trend.length / 2);
    const early = trend.slice(0, half);
    const late = trend.slice(half);
    const avg = (xs: typeof trend) =>
      xs.reduce((s, p) => s + p.real, 0) / Math.max(1, xs.length);
    trendDelta = avg(late) - avg(early);
  }
  return {
    modulesActive,
    modulesMeasurable,
    modulesNearImpact,
    clusters: data.signal_clusters ?? [],
    strongestCluster: data.strongest_active_cluster ?? null,
    geoCluster: data.strongest_geo_concentration ?? null,
    topGaps: data.gap_summary?.by_kind ?? [],
    nearImpact: data.near_impact ?? [],
    realPct: data.global_metrics?.real_pct ?? 0,
    narrativePct: data.global_metrics?.narrative_pct ?? 0,
    totalSignals: data.global_metrics?.total_signals ?? 0,
    measurableItems: data.system_impact?.measurable_black_items ?? 0,
    blackItems: data.system_impact?.black_relevant_items ?? 0,
    capitalPct: data.system_impact?.capital_black_pct ?? 0,
    policyImpactPct: data.system_impact?.policy_black_impact_pct ?? 0,
    outcomeGaps: data.system_impact?.outcome_gaps ?? 0,
    trendDelta,
  };
}

function geoPhrase(cluster: SignalCluster | null): string | null {
  if (!cluster) return null;
  const { city, state } = cluster.geo;
  if (city && state) return `${city}, ${state}`;
  if (state) return state;
  if (city) return city;
  return null;
}

function joinModules(modules: string[]): string {
  const m = modules.filter((x): x is string => Boolean(x)).slice(0, 3);
  if (m.length === 0) return "Signals";
  if (m.length === 1) return capitalize(m[0]!);
  if (m.length === 2) return `${capitalize(m[0]!)} and ${capitalize(m[1]!)}`;
  return `${capitalize(m[0]!)}, ${capitalize(m[1]!)}, and ${capitalize(m[2]!)}`;
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

// Detect institutional alignment pairs that read as coordination rather
// than coincidence.
const ALIGNMENT_PAIRS: Array<[string, string, string]> = [
  ["education", "workforce", "training pipelines and placement"],
  ["capital", "workforce", "funded job creation"],
  ["policy", "workforce", "policy-backed workforce expansion"],
  ["policy", "legal", "enforcement of policy commitments"],
  ["capital", "education", "institutional investment in schools"],
];

function detectAlignment(modules: Set<string>): {
  pair: [string, string];
  subject: string;
} | null {
  for (const [a, b, subject] of ALIGNMENT_PAIRS) {
    if (modules.has(a) && modules.has(b)) {
      return { pair: [a, b], subject };
    }
  }
  return null;
}

export function classifyIntelligenceMode(
  data: OverviewResponse,
): IntelligenceModeResolved {
  const ctx = buildContext(data);
  const geo = geoPhrase(ctx.geoCluster ?? ctx.strongestCluster);

  // 1) Emerging opportunity — a single module has near-impact signals
  // concentrating, regardless of broader system state.
  const nearImpactSorted = [...ctx.modulesNearImpact.entries()].sort(
    (a, b) => b[1] - a[1],
  );
  const nearTopEntry = nearImpactSorted[0];
  if (nearTopEntry && nearTopEntry[1] >= 2) {
    const [mod, count] = nearTopEntry;
    const geoTail = geo ? ` around ${geo}` : "";
    return {
      mode: "emerging_opportunity",
      label: "Emerging opportunity",
      tone: "active",
      headline: `Near-impact ${mod} signals are forming${geoTail}.`,
      subline: `${count} ${mod} item${count === 1 ? "" : "s"} approaching deployment or outcome — the window to act is narrowing.`,
      focus: `Track ${mod} for verifiable outcomes`,
      driving_modules: [mod],
      geo_phrase: geo,
    };
  }

  // Phase 37 — if the strongest cluster is symbolic / procedural, the
  // surface should name that explicitly instead of falling into
  // "coordination" or "fragmentation". This blocks ceremonial /
  // ritual / procedural overlap from masquerading as alignment.
  const strongest = ctx.strongestCluster;
  const strongestType = strongest?.convergence_type;
  if (strongest && strongestType === "symbolic") {
    const geoTail = geoPhrase(strongest) ? ` in ${geoPhrase(strongest)}` : "";
    return {
      mode: "symbolic_activity",
      label: "Symbolic activity",
      tone: "quiet",
      headline: `Most policy activity${geoTail} is commemorative, not enforceable.`,
      subline: `${strongest.event_count} items in the dominant cluster are recognitions, resolutions, or awareness windows — not leverage.`,
      focus: `Discount ceremonial signals; track only enforceable action`,
      driving_modules: strongest.modules,
      geo_phrase: geoPhrase(strongest),
    };
  }
  if (strongest && strongestType === "procedural") {
    const geoTail = geoPhrase(strongest) ? ` in ${geoPhrase(strongest)}` : "";
    return {
      mode: "procedural_activity",
      label: "Procedural activity",
      tone: "watch",
      headline: `${joinModules(strongest.modules)} activity${geoTail} is moving through procedure, not outcome.`,
      subline: `Hearings, introductions, and procedural steps — no funding, enforcement, or deployment yet.`,
      focus: `Track whether procedure converts into enforceable action`,
      driving_modules: strongest.modules,
      geo_phrase: geoPhrase(strongest),
    };
  }
  if (strongest && strongestType === "narrative") {
    const geoTail = geoPhrase(strongest) ? ` in ${geoPhrase(strongest)}` : "";
    return {
      mode: "performative_activity",
      label: "Narrative cluster",
      tone: "blocked",
      headline: `${joinModules(strongest.modules)} signals${geoTail} are bound by coverage, not action.`,
      subline: `${strongest.event_count} items in the dominant cluster are narrative-driven — discount until real activity surfaces.`,
      focus: `Wait for capital, policy, or legal evidence before trusting the read`,
      driving_modules: strongest.modules,
      geo_phrase: geoPhrase(strongest),
    };
  }

  // 2) Coordination / institutional alignment — multiple aligned modules
  // around a shared subject. Coordination = same cluster; alignment =
  // structurally paired modules. Phase 37 — gated on strategic
  // convergence so we don't promote weak clusters into "alignment."
  const cluster = ctx.strongestCluster;
  const clusterIsStrategic =
    !cluster?.convergence_type || cluster.convergence_type === "strategic";
  // Phase 37 — clusters with relevance < 0.45 read as weak alignment,
  // not coordination. Surfaces the right read without faking confidence.
  if (
    cluster &&
    clusterIsStrategic &&
    (cluster.strategic_relevance_score ?? 0.6) < 0.45 &&
    cluster.modules.length >= 2
  ) {
    const geoTail = geoPhrase(cluster) ? ` in ${geoPhrase(cluster)}` : "";
    return {
      mode: "weak_alignment",
      label: "Weak alignment",
      tone: "watch",
      headline: `${joinModules(cluster.modules)} signals${geoTail} share entities but lack leverage.`,
      subline: `Convergence is real but the items in it aren't yet funded, deployed, or enforced — alignment without traction.`,
      focus: `Identify what would convert the alignment into action`,
      driving_modules: cluster.modules,
      geo_phrase: geoPhrase(cluster),
    };
  }
  if (cluster && clusterIsStrategic && cluster.modules.length >= 2) {
    const alignment = detectAlignment(new Set(cluster.modules));
    if (alignment) {
      const geoTail = geoPhrase(cluster) ? ` in ${geoPhrase(cluster)}` : "";
      return {
        mode: "institutional_alignment",
        label: "Institutional alignment",
        tone: "active",
        headline: `${capitalize(alignment.pair[0])} and ${alignment.pair[1]} systems are aligning around ${alignment.subject}${geoTail}.`,
        subline: `Cross-module convergence detected — this is the kind of alignment that precedes durable change.`,
        focus: `Identify which institutions own the alignment`,
        driving_modules: cluster.modules,
        geo_phrase: geoPhrase(cluster),
      };
    }
    if (cluster.modules.length >= 3) {
      const geoTail = geoPhrase(cluster) ? ` in ${geoPhrase(cluster)}` : "";
      return {
        mode: "coordination",
        label: "Coordination",
        tone: "active",
        headline: `${joinModules(cluster.modules)} are moving in coordination${geoTail}.`,
        subline: `${cluster.event_count} events across ${cluster.modules.length} modules share entities and geography — coordinated action, not coincidence.`,
        focus: `Map the entities driving the coordination`,
        driving_modules: cluster.modules,
        geo_phrase: geoPhrase(cluster),
      };
    }
  }

  // 3) Acceleration — multiple modules active AND trend rising.
  if (ctx.modulesActive.size >= 3 && ctx.trendDelta > 0.5) {
    const top = cluster?.modules?.slice(0, 2) ?? [...ctx.modulesActive].slice(0, 2);
    const geoTail = geo ? ` in ${geo}` : "";
    return {
      mode: "acceleration",
      label: "Acceleration",
      tone: "active",
      headline: `${joinModules(top)} activity ${geoTail ? "are" : "is"} accelerating together${geoTail}.`,
      subline: `Volume rising across ${ctx.modulesActive.size} modules in this window — watch for inflection.`,
      focus: `Watch for whether velocity converts to outcome`,
      driving_modules: top,
      geo_phrase: geo,
    };
  }

  // 4) Suppression — policy or legal activity present but
  // outcomes/enforcement notably absent.
  const policyGap = ctx.topGaps.find(
    (g) =>
      g.kind === "policy_without_black_outcome" ||
      (g.modules?.includes("policy") &&
        (g.severity === "critical" || g.severity === "high")),
  );
  const legalActive =
    ctx.modulesActive.has("legal") &&
    !ctx.modulesMeasurable.has("legal") &&
    ctx.totalSignals > 0;
  if (policyGap || legalActive) {
    // Phase 37 — distinguish "suppression" (something is actively
    // blocking enforcement) from "enforcement_mismatch" (enforcement
    // is happening but landing on the wrong things, or remedy doesn't
    // match the action). The latter shows up when legal/enforcement
    // gaps are present at high severity but policy is also moving.
    const enforcementGap = ctx.topGaps.find(
      (g) =>
        g.kind === "enforcement_without_outcome" ||
        g.kind === "settlement_without_change" ||
        g.kind === "legal_action_without_reform",
    );
    if (enforcementGap && ctx.modulesActive.has("legal")) {
      return {
        mode: "enforcement_mismatch",
        label: "Enforcement mismatch",
        tone: "blocked",
        headline: `Enforcement is happening, but remedy is not matching the action.`,
        subline:
          enforcementGap.why ||
          "Cases, settlements, or investigations exist without observable reform or outcome.",
        focus: `Track which actors own the remedy gap`,
        driving_modules: enforcementGap.modules ?? ["legal"],
        geo_phrase: geo,
      };
    }
    const driver = policyGap ? "Policy" : "Legal";
    return {
      mode: "suppression",
      label: "Suppression",
      tone: "blocked",
      headline: `${driver} movement is occurring, but enforcement pressure is limiting impact.`,
      subline:
        policyGap
          ? policyGap.why || "Action exists; enforcement and outcome do not."
          : "Cases and filings present; remedies and reforms are not landing.",
      focus: `Identify who is blocking enforcement`,
      driving_modules: policyGap?.modules ?? ["legal"],
      geo_phrase: geo,
    };
  }

  // 5) Strategic silence — capital activity without proportionate
  // narrative coverage.
  if (ctx.capitalPct >= 20 && ctx.narrativePct < 25 && ctx.totalSignals > 0) {
    return {
      mode: "strategic_silence",
      label: "Strategic silence",
      tone: "watch",
      headline: `Capital movement exists with minimal public narrative.`,
      subline: `${ctx.capitalPct}% of capital signals touch Black communities while narrative volume stays low — money is moving quietly.`,
      focus: `Follow the capital, not the coverage`,
      driving_modules: ["capital"],
      geo_phrase: geo,
    };
  }

  // 6) Performative activity — narrative dominates real activity.
  if (ctx.narrativePct >= 55 && ctx.realPct <= 35) {
    return {
      mode: "performative_activity",
      label: "Performative activity",
      tone: "blocked",
      headline: `Coverage and announcements are outpacing real activity.`,
      subline: `${ctx.narrativePct}% narrative vs ${ctx.realPct}% real — the public picture and the ground truth are diverging.`,
      focus: `Discount narrative-only signals until evidence appears`,
      driving_modules: ["media"],
      geo_phrase: geo,
    };
  }

  // 7) Momentum — rising real activity even without strong cluster.
  if (ctx.trendDelta > 0.5 && ctx.totalSignals > 5) {
    return {
      mode: "momentum",
      label: "Momentum",
      tone: "active",
      headline: `Real activity is rising across the system.`,
      subline: `Volume trending up over the window — early-stage momentum, not yet outcome.`,
      focus: `Watch for which module breaks into measurable impact first`,
      driving_modules: [...ctx.modulesActive].slice(0, 3),
      geo_phrase: geo,
    };
  }

  // 8) Fragmentation — multiple active modules, no shared cluster,
  // multiple distinct gaps. This replaces the old "everything is
  // failing" default.
  if (ctx.modulesActive.size >= 3 && ctx.topGaps.length >= 2) {
    const gapModules = new Set<string>();
    for (const g of ctx.topGaps) for (const m of g.modules ?? []) gapModules.add(m);
    return {
      mode: "fragmentation",
      label: "Fragmentation",
      tone: "blocked",
      headline: `${joinModules([...gapModules])} signals are disconnected.`,
      subline: `${ctx.modulesActive.size} modules active but moving on separate tracks — no shared entity, geography, or program is binding them.`,
      focus: `Find the missing link between the active modules`,
      driving_modules: [...gapModules].slice(0, 3),
      geo_phrase: geo,
    };
  }

  // Default — fragmentation read with quieter tone when activity is thin.
  return {
    mode: "fragmentation",
    label: "Fragmentation",
    tone: "quiet",
    headline: `System activity is scattered with no dominant signal.`,
    subline: `${ctx.totalSignals} signal${ctx.totalSignals === 1 ? "" : "s"} in window — nothing is converging.`,
    focus: null,
    driving_modules: [...ctx.modulesActive].slice(0, 3),
    geo_phrase: geo,
  };
}
